// Main App Component with Routing

import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Provider } from 'react-redux';
import store from './redux/store';
import LoginPage from './pages/LoginPage.jsx';
import ForgotPasswordPage from './pages/ForgotPasswordPage.jsx';
import SupervisorLoginPage from './pages/SupervisorLoginPage.jsx';
import TodayPage from './pages/TodayPage.jsx';
import TodayInquiriesPage from './pages/TodayInquiriesPage.jsx';
import DashboardPage from './pages/DashboardPage.jsx';
import ProductsPage from './pages/ProductsPage.jsx';
import ProductDetailPage from './pages/ProductDetailPage.jsx';
import NotAvailablePage from './pages/NotAvailablePage.jsx';
import ReturnsPage from './pages/ReturnsPage.jsx';
import PaymentsPage from './pages/PaymentsPage.jsx';
import MoneyInvestmentsPage from './pages/MoneyInvestmentsPage.jsx';
import MoneyInvestmentDetailPage from './pages/MoneyInvestmentDetailPage.jsx';
import OrdersPage from './pages/OrdersPage.jsx';
import CustomerOrdersPage from './pages/CustomerOrdersPage.jsx';
import CustomersPage from './pages/CustomersPage.jsx';
import SupervisorsPage from './pages/SupervisorsPage.jsx';
import SupervisorAnalyticsPage from './pages/SupervisorAnalyticsPage.jsx';
import SalespersonsPage from './pages/SalespersonsPage.jsx';
import SalespersonAnalyticsPage from './pages/SalespersonAnalyticsPage.jsx';
import TemplatesPage from './pages/TemplatesPage.jsx';
import ExchangePartsPage from './pages/ExchangePartsPage.jsx';
// import WhatsAppSettingsPage from './pages/WhatsAppSettingsPage.jsx';
import AnalyticsPage from './pages/AnalyticsPage.jsx';
import SettingsPage from './pages/SettingsPage.jsx';
import ExpensesPage from './pages/ExpensesPage.jsx';
import DispatchPage from './pages/DispatchPage.jsx';
import OsmPage from './pages/OsmPage.jsx';
import ExpenseReportPage from './pages/ExpenseReportPage.jsx';
import PriceChangesPage from './pages/PriceChangesPage.jsx';
import TradeInsPage from './pages/TradeInsPage.jsx';
import OutsidePartsPage from './pages/OutsidePartsPage.jsx';
import CarsPage from './pages/CarsPage.jsx';
import CarDetailPage from './pages/CarDetailPage.jsx';
import CarsStaffPage from './pages/CarsStaffPage.jsx';
import CarsWorkPage from './pages/CarsWorkPage.jsx';
import DeletedRecordsPage from './pages/DeletedRecordsPage.jsx';
import SupervisorSaleRemindersPage from './pages/SupervisorSaleRemindersPage.jsx';
import OpsHomePage from './pages/ops/OpsHomePage.jsx';
import OpsDispatchPage from './pages/ops/OpsDispatchPage.jsx';
import OpsCounterPage from './pages/ops/OpsCounterPage.jsx';
import OpsPaymentsPage from './pages/ops/OpsPaymentsPage.jsx';
import ProtectedRoute from './components/auth/ProtectedRoute.jsx';
import { ROUTES, STORAGE_KEYS } from './constants/app.constants';
import StorageUtil from './utils/storage';
import useAuthPresence from './hooks/useAuthPresence';
import { recordActivity } from './utils/activityBeacon';

// Each guard watches ITS OWN realm's keys, so a sign-out in another tab
// re-evaluates this gate immediately — instead of the tab keeping a stale
// answer until some API call 401s mid-click. Three logins, three gates,
// no realm ever reads another's keys.
const ExpensesProtectedRoute = ({ children }) => {
  useAuthPresence([STORAGE_KEYS.EXPENSES_ACCESS_TOKEN]);
  if (!StorageUtil.isExpensesAuthenticated()) {
    return <Navigate to="/login?tab=expenses" replace />;
  }
  return children;
};

// The cars team's own session — its own token, so being signed in as
// superadmin does NOT let you in here, and vice versa.
const CarsProtectedRoute = ({ children }) => {
  useAuthPresence([STORAGE_KEYS.CARS_ACCESS_TOKEN]);
  if (!StorageUtil.isCarsAuthenticated()) {
    return <Navigate to="/login?tab=cars" replace />;
  }
  return children;
};

// Every tab feeds the shared idle clock, whichever workspace it shows. The
// superadmin's 30-minute logout consults this beacon, so working in the
// Admin or Cars tab still counts as being at the keyboard — only a browser
// with NO activity in any tab idles out.
const ACTIVITY_EVENTS = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll', 'click'];

function App() {
  React.useEffect(() => {
    const record = () => recordActivity();
    ACTIVITY_EVENTS.forEach((evt) => window.addEventListener(evt, record, { passive: true }));
    return () => ACTIVITY_EVENTS.forEach((evt) => window.removeEventListener(evt, record));
  }, []);

  return (
    <Provider store={store}>
      <Router>
        <Routes>
          {/* Auth Routes */}
          <Route path={ROUTES.LOGIN} element={<LoginPage />} />
          <Route path={ROUTES.FORGOT_PASSWORD} element={<ForgotPasswordPage />} />
          <Route path={ROUTES.SUPERVISOR_LOGIN} element={<SupervisorLoginPage />} />

          {/* Protected Routes — Today is the landing screen: what needs the
              admin now. The charts live on Business Overview (/dashboard). */}
          <Route
            path={ROUTES.TODAY}
            element={
              <ProtectedRoute>
                <TodayPage />
              </ProtectedRoute>
            }
          />
          <Route
            path={ROUTES.TODAY_INQUIRIES}
            element={
              <ProtectedRoute>
                <TodayInquiriesPage />
              </ProtectedRoute>
            }
          />
          <Route
            path={ROUTES.DASHBOARD}
            element={
              <ProtectedRoute>
                <DashboardPage />
              </ProtectedRoute>
            }
          />
          <Route
            path={ROUTES.PRODUCTS}
            element={
              <ProtectedRoute>
                <ProductsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path={ROUTES.PRODUCT_DETAIL}
            element={
              <ProtectedRoute>
                <ProductDetailPage />
              </ProtectedRoute>
            }
          />
          <Route
            path={ROUTES.NOT_AVAILABLE}
            element={
              <ProtectedRoute>
                <NotAvailablePage />
              </ProtectedRoute>
            }
          />
          <Route
            path={ROUTES.RETURNS}
            element={
              <ProtectedRoute>
                <ReturnsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path={ROUTES.PAYMENTS}
            element={
              <ProtectedRoute>
                <PaymentsPage />
              </ProtectedRoute>
            }
          />
          {/* Planned Payments merged into Money — salary/rent and chits are one
              section now, so old links land there instead of 404ing. */}
          <Route
            path={ROUTES.PLANNED_PAYMENTS}
            element={<Navigate to={ROUTES.MONEY_INVESTMENTS} replace />}
          />
          <Route
            path={ROUTES.MONEY_INVESTMENTS}
            element={
              <ProtectedRoute>
                <MoneyInvestmentsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path={ROUTES.MONEY_INVESTMENT_DETAIL}
            element={
              <ProtectedRoute>
                <MoneyInvestmentDetailPage />
              </ProtectedRoute>
            }
          />
          <Route
            path={ROUTES.DISPATCH}
            element={
              <ProtectedRoute>
                <DispatchPage />
              </ProtectedRoute>
            }
          />
          <Route
            path={ROUTES.OSM}
            element={
              <ProtectedRoute>
                <OsmPage />
              </ProtectedRoute>
            }
          />
          <Route
            path={ROUTES.EXPENSE_REPORT}
            element={
              <ProtectedRoute>
                <ExpenseReportPage />
              </ProtectedRoute>
            }
          />
          <Route
            path={ROUTES.PRICE_CHANGES}
            element={
              <ProtectedRoute>
                <PriceChangesPage />
              </ProtectedRoute>
            }
          />
          <Route
            path={ROUTES.DELETED_RECORDS}
            element={
              <ProtectedRoute>
                <DeletedRecordsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path={ROUTES.TRADE_INS}
            element={
              <ProtectedRoute>
                <TradeInsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path={ROUTES.OUTSIDE_PARTS}
            element={
              <ProtectedRoute>
                <OutsidePartsPage />
              </ProtectedRoute>
            }
          />
          {/* Cars — the whole-car business. CARS_STAFF must stay ABOVE
              CAR_DETAIL, or "/cars/team" is read as a car with the id "team". */}
          <Route
            path={ROUTES.CARS_STAFF}
            element={
              <ProtectedRoute>
                <CarsStaffPage />
              </ProtectedRoute>
            }
          />
          <Route
            path={ROUTES.CARS}
            element={
              <ProtectedRoute>
                <CarsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path={ROUTES.CAR_DETAIL}
            element={
              <ProtectedRoute>
                <CarDetailPage />
              </ProtectedRoute>
            }
          />
          <Route
            path={ROUTES.ORDERS}
            element={
              <ProtectedRoute>
                <OrdersPage />
              </ProtectedRoute>
            }
          />
          <Route
            path={ROUTES.CUSTOMER_ORDERS}
            element={
              <ProtectedRoute>
                <CustomerOrdersPage />
              </ProtectedRoute>
            }
          />
          <Route
            path={ROUTES.CUSTOMERS}
            element={
              <ProtectedRoute>
                <CustomersPage />
              </ProtectedRoute>
            }
          />
          <Route
            path={ROUTES.SUPERVISORS}
            element={
              <ProtectedRoute>
                <SupervisorsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path={ROUTES.SUPERVISOR_ANALYTICS}
            element={
              <ProtectedRoute>
                <SupervisorAnalyticsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path={ROUTES.SALESPERSONS}
            element={
              <ProtectedRoute>
                <SalespersonsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path={ROUTES.SALESPERSON_ANALYTICS}
            element={
              <ProtectedRoute>
                <SalespersonAnalyticsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path={ROUTES.TEMPLATES}
            element={
              <ProtectedRoute>
                <TemplatesPage />
              </ProtectedRoute>
            }
          />
          <Route
            path={ROUTES.EXCHANGE_PARTS}
            element={
              <ProtectedRoute>
                <ExchangePartsPage />
              </ProtectedRoute>
            }
          />
          <Route path={ROUTES.SALE_REMINDERS} element={<ProtectedRoute><SupervisorSaleRemindersPage /></ProtectedRoute>} />
          <Route
            path={ROUTES.ANALYTICS}
            element={
              <ProtectedRoute>
                <AnalyticsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path={ROUTES.SETTINGS}
            element={
              <ProtectedRoute>
                <SettingsPage />
              </ProtectedRoute>
            }
          />

          {/* ADMIN (operations) workspace — separate auth (expensadmin login):
              dispatch, counter sales (walk-in + personal), expenses */}
          <Route
            path={ROUTES.EXPENSES}
            element={
              <ExpensesProtectedRoute>
                <ExpensesPage />
              </ExpensesProtectedRoute>
            }
          />
          <Route
            path={ROUTES.OPS_HOME}
            element={
              <ExpensesProtectedRoute>
                <OpsHomePage />
              </ExpensesProtectedRoute>
            }
          />
          <Route
            path={ROUTES.OPS_DISPATCH}
            element={
              <ExpensesProtectedRoute>
                <OpsDispatchPage />
              </ExpensesProtectedRoute>
            }
          />
          <Route
            path={ROUTES.OPS_COUNTER}
            element={
              <ExpensesProtectedRoute>
                <OpsCounterPage />
              </ExpensesProtectedRoute>
            }
          />
          <Route
            path={ROUTES.OPS_PAYMENTS}
            element={
              <ExpensesProtectedRoute>
                <OpsPaymentsPage />
              </ExpensesProtectedRoute>
            }
          />

          {/* CARS TEAM workspace — their own login (Cars tab on /login) */}
          <Route
            path={ROUTES.CARS_WORK}
            element={
              <CarsProtectedRoute>
                <CarsWorkPage />
              </CarsProtectedRoute>
            }
          />

          {/* Catch all - redirect to login */}
          <Route path="/" element={<Navigate to={ROUTES.TODAY} replace />} />
          <Route path="*" element={<Navigate to={ROUTES.LOGIN} replace />} />
        </Routes>
      </Router>
    </Provider>
  );
}

export default App;
